import { useRef, useEffect, useCallback } from 'react';
import type { Stroke, StrokeInput, Point } from '@twitch-draw/shared';
import {
  renderStrokes,
  renderCurrentStroke,
  clearCanvas,
  normalizeCoordinates,
} from '../canvas/renderer';

interface DrawCanvasProps {
  strokes: Stroke[];
  currentStroke: StrokeInput | null;
  onStartStroke: (point: Point) => void;
  onContinueStroke: (point: Point) => void;
  onEndStroke: () => void;
  disabled?: boolean;
  isEraser?: boolean;
  color?: string;
}

function createPencilCursor(color: string): string {
  const svg = `<svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
    <g fill="none" fill-rule="evenodd">
      <path d="m0 0h32v32h-32z"/>
      <path d="m13.4972619-.65685425h4c1.1045695 0 2 .8954305 2 2v23.66314045c0 .6440327-.1555097 1.2785431-.4533088 1.8495895l-3.0006899 5.7539905c-.153225.2938175-.5156245.4077902-.8094421.2545652-.1127518-.0587998-.2039066-.1519019-.2603101-.2658713l-3.0612398-6.1855586c-.2729885-.5516022-.4150093-1.1587586-.4150093-1.7742159v-23.29563985c0-1.1045695.8954305-2 2-2z" fill="${color}" transform="matrix(.70710678 .70710678 -.70710678 .70710678 16.206305 -6.125481)"/>
    </g>
  </svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 0 24, crosshair`;
}

export function DrawCanvas({
  strokes,
  currentStroke,
  onStartStroke,
  onContinueStroke,
  onEndStroke,
  disabled = false,
  isEraser = false,
  color = '#ff0055',
}: DrawCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(dpr, dpr);
    }
  }, []);

  useEffect(() => {
    resizeCanvas();

    const observer = new ResizeObserver(resizeCanvas);
    if (canvasRef.current) {
      observer.observe(canvasRef.current);
    }

    return () => observer.disconnect();
  }, [resizeCanvas]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    clearCanvas(ctx);
    renderStrokes(ctx, strokes, rect.width, rect.height);
    renderCurrentStroke(ctx, currentStroke, rect.width, rect.height);
  }, [strokes, currentStroke]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (disabled) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    isDrawingRef.current = true;
    canvas.setPointerCapture(e.pointerId);

    const point = normalizeCoordinates(e.clientX, e.clientY, canvas);
    onStartStroke(point);
  }, [disabled, onStartStroke]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDrawingRef.current || disabled) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const point = normalizeCoordinates(e.clientX, e.clientY, canvas);
    onContinueStroke(point);
  }, [disabled, onContinueStroke]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (!isDrawingRef.current) return;

    isDrawingRef.current = false;
    canvasRef.current?.releasePointerCapture(e.pointerId);
    onEndStroke();
  }, [onEndStroke]);

  const handlePointerLeave = useCallback((e: React.PointerEvent) => {
    if (!isDrawingRef.current) return;

    isDrawingRef.current = false;
    canvasRef.current?.releasePointerCapture(e.pointerId);
    onEndStroke();
  }, [onEndStroke]);

  return (
    <canvas
      ref={canvasRef}
      className="draw-canvas"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerLeave}
      style={{
        touchAction: 'none',
        cursor: disabled
          ? 'default'
          : isEraser
            ? 'url(./eraser.png) 12 12, crosshair'
            : createPencilCursor(color)
      }}
    />
  );
}
